import React, { useState } from "react";
import { SpacingColumn } from "../../components/BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../../components/BuildingBlocks/SpacingRow";
import { MediaCompSwitch } from "../../components/BuildingBlocks/MediaCompSwitch";
import { FrontQuestionFormCard } from "../../components/Front/Complex/FrontQuestionFormCard/FrontQuestionFormCard";
import FrontParagraphSmall from "../../components/Front/Simple/FrontParagraphSmall/FrontParagraphSmall";
import { FrontRadioList } from "../../components/Front/Simple/FrontRadioList/FrontRadioList";
import FrontInputLabelAndErrorWrapComponent from "../../components/Front/Simple/FrontInputLabelAndErrorWrapComponent/FrontInputLabelAndErrorWrapComponent";
import { FrontFormPrimaryButton } from "../../components/Front/Simple/FrontFormPrimaryButton/FrontFormPrimaryButton";
import { FrontInfoBubble } from "../../components/Front/Simple/FrontInfoBubble/FrontInfoBubble";
import { WeightBold } from "../../components/WeightBold/WeightBold";
import { FrontColors } from "../../models/FrontColors";
import { FrontSpacing } from "../../models/FrontSpacing";
import { TContainerStateProps } from "../../state/TContainerStateProps";
import { TOnboardingTabStatus } from "../../components/Front/Simple/FrontOnboardingTab/FrontOnboardingTab";
import { ClientCaseDivorceDocumentForm } from "../../../../domain/codecs/form/ClientCaseForm";
import { TCaseDivorceDocumentType1 } from "../../../../domain/codecs/CaseDivorceDocumentType";

type TDivorceDocumentRealType = Exclude<TCaseDivorceDocumentType1, "unknown">;

const optionDescriptions: Array<{ value: TDivorceDocumentRealType, label: string, description: string }> = [
    {
        value: "separation_agreement",
        label: "Separation agreement",
        description: "A written agreement between you and your ex about how you've split things. Often signed before the divorce is finalised.",
    },
    {
        value: "consent_order",
        label: "Consent order",
        description: "The court-approved agreement about how you've split the money.",
    },
    {
        value: "final_order",
        label: "Final order",
        description: "The court document that officially ends the marriage. For divorces started after April 2022.",
    },
    {
        value: "court_petition",
        label: "Court petition or divorce application",
        description: "The form that started the divorce. Sometimes called a petition, sometimes called an application.",
    },
    {
        value: "statement_of_truth",
        label: "Statement of truth",
        description: "A signed statement you can use when none of the other documents are ready yet.",
    },
];

const FrontDivorceDocumentContainer = (props: TContainerStateProps): JSX.Element => {
    const form = props.state.forms.client_case_page.children.divorce_document || ClientCaseDivorceDocumentForm.newDefault();

    const isStepComplete = form.children.is_step_complete;
    const selectedValue = form.edited.divorce_document_type;
    const isRealValueSelected = selectedValue !== "unknown";

    const [displayValidationErrorsIfPresent, setDisplayValidationErrorsIfPresent] = useState(false);

    const onChange = (value: TDivorceDocumentRealType) => {
        setDisplayValidationErrorsIfPresent(false);
        props.dispatch({
            type: "CLIENT_DIVORCE_DOCUMENT_CHANGE",
            payload: {
                ...form,
                edited: {
                    ...form.edited,
                    divorce_document_type: value,
                },
            },
        });
    };

    const onSubmit = () => {
        if (!isRealValueSelected) {
            setDisplayValidationErrorsIfPresent(true);
            return;
        }
        props.dispatch({ type: "CLIENT_DIVORCE_DOCUMENT_SUBMIT" });
    };

    return (
        <FrontQuestionFormCard
            title="Tell us about your divorce document"
        >
            <SpacingColumn spacing={FrontSpacing.LARGE_1}>

                <SpacingColumn spacing={FrontSpacing.SMALL_2}>
                    <FrontParagraphSmall>
                        We need to see one of these divorce documents so we can register the property in your name. Pick the one you'll be able to give us a copy of. Your solicitor will tell you when they need it.
                    </FrontParagraphSmall>
                </SpacingColumn>

                <FrontInputLabelAndErrorWrapComponent
                    label="Which document will you give us?"
                    errorMessage="Please pick one of the options."
                    displayError={displayValidationErrorsIfPresent && !isRealValueSelected}
                >
                    <FrontRadioList
                        value={isRealValueSelected ? selectedValue : null}
                        options={optionDescriptions.map(({ value, label }) => ({ value, label }))}
                        onChange={onChange}
                    />
                </FrontInputLabelAndErrorWrapComponent>

                {isRealValueSelected && (
                    <FrontInfoBubble color={FrontColors.PRIMARY_22}>
                        <FrontParagraphSmall>
                            {optionDescriptions.find((o) => o.value === selectedValue)?.description}
                        </FrontParagraphSmall>
                    </FrontInfoBubble>
                )}

                <SpacingColumn spacing={FrontSpacing.MEDIUM_1}>
                    <MediaCompSwitch
                        DesktopComponent={SpacingRow}
                        desktopProps={{ spacing: FrontSpacing.SMALL_3 }}
                        MobileComponent={SpacingColumn}
                        mobileProps={{ spacing: FrontSpacing.SMALL_3 }}
                        breakpoint="600"
                    >
                        <FrontFormPrimaryButton
                            label={isStepComplete ? "Update" : "Tell us"}
                            icon="done"
                            isDisabled={false}
                            onClick={onSubmit}
                        />
                    </MediaCompSwitch>

                    {displayValidationErrorsIfPresent && !isRealValueSelected && (
                        <FrontInfoBubble color={FrontColors.ERROR_FIREBRICK_22}>
                            <WeightBold>Please pick a document so we can save this step.</WeightBold> If you're not sure which one you have, come back later once you've checked.
                        </FrontInfoBubble>
                    )}
                </SpacingColumn>
            </SpacingColumn>
        </FrontQuestionFormCard>
    );
};

export const getDivorceDocumentStatus = (props: TContainerStateProps): TOnboardingTabStatus => {
    const form = props.state.forms.client_case_page.children.divorce_document;
    if (form && form.children.is_step_complete) {
        return "completed";
    }
    return "active";
};

export default FrontDivorceDocumentContainer;
